import { CheckCheck, CheckSquare, MessageSquare } from "lucide-react"

import { ActionDefinition } from "@/components/automations/types"

export const taskActions: ActionDefinition[] = [
  {
    key: "action__record_create_task",
    name: "Create task",
    description: "Create a new task",
    category: "task",
    nodeType: "action",
    icon: CheckSquare,
    providesSubjects: ["task"],
    chatFields: [
      {
        key: "due_rule",
        label: "Calendar due date",
        inputType: "json",
        required: false,
        chatValueShape:
          'Null for a relative date, or {"kind":0,"weekday":1} (Sunday=0), {"kind":1,"day":1} (day may be "last" for month end), or {"kind":2,"month":4,"day":15}. Includes today; monthly dates skip months without that day. Month/day must exist every year.',
      },
    ],
    fields: [
      {
        key: "title",
        label: "Title",
        inputType: "string",
        required: true,
        supportsInterpolation: true,
      },
      {
        key: "description",
        label: "Description",
        inputType: "markdown",
        required: false,
        supportsInterpolation: true,
      },
      {
        key: "email_content",
        label: "Email draft",
        inputType: "task_email_content",
        required: false,
        optionsSource: "email_templates",
        chatValueShape:
          'Either {"email_template_id":"<id>"} or {"email_template_id":null,"email_prompt":{"markdown":"..."}}',
        supportsInterpolation: true,
        interpolationHelperText: "The AI email prompt supports variables from subject data and prior step results.",
      },
      {
        key: "due_in",
        label: "Due in",
        inputType: "number",
        required: true,
        defaultValue: 1,
      },
      {
        key: "repeat",
        label: "Repeats",
        inputType: "select",
        required: false,
        placeholder: "Does not repeat",
        chatValueShape:
          'Empty or "<number>:<unit>" with a number from 1 to 12 and unit days, weeks, or months, or 1 to 2 with unit years',
      },
      {
        key: "priority",
        label: "Priority",
        inputType: "select",
        required: false,
        options: [
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
          { value: "urgent", label: "Urgent" },
        ],
      },
      {
        key: "assignee",
        label: "Assigned to",
        inputType: "assignment_picker",
        required: false,
        optionsSource: "assignments",
      },
      {
        key: "tag_id",
        label: "Label",
        inputType: "select",
        required: false,
        optionsSource: "task_tags",
      },
    ],
  },
  {
    key: "action__update_tasks",
    name: "Update tasks",
    description: "Update matching tasks on this record",
    category: "task",
    nodeType: "action",
    recordType: "task",
    icon: CheckCheck,
    requiresAnySubject: ["household", "contact", "project", "task"],
    fields: [
      {
        key: "filter_status",
        label: "Current status",
        inputType: "select",
        required: false,
        placeholder: "Any status",
        options: [
          { value: "pending", label: "Pending" },
          { value: "todo", label: "To do" },
          { value: "done", label: "Done" },
          { value: "rejected", label: "Rejected" },
          { value: "scheduled", label: "Scheduled" },
        ],
      },
      {
        key: "filter_assignee",
        label: "Currently assigned to",
        inputType: "assignment_picker",
        required: false,
        optionsSource: "assignments",
        assignmentTypes: ["user", "custom_field"],
      },
      {
        key: "filter_priority",
        label: "Current priority",
        inputType: "select",
        required: false,
        placeholder: "Any priority",
        options: [
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
          { value: "urgent", label: "Urgent" },
        ],
      },
      {
        key: "status",
        label: "Status",
        inputType: "select",
        required: false,
        placeholder: "No change",
        options: [
          { value: "todo", label: "To do" },
          { value: "done", label: "Done" },
          { value: "rejected", label: "Rejected" },
        ],
      },
      {
        key: "priority",
        label: "Priority",
        inputType: "select",
        required: false,
        placeholder: "No change",
        options: [
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
          { value: "urgent", label: "Urgent" },
        ],
      },
      {
        key: "assignee",
        label: "Assign to",
        inputType: "assignment_picker",
        required: false,
        optionsSource: "assignments",
      },
      {
        key: "due_in",
        label: "Due in",
        inputType: "select",
        required: false,
        placeholder: "No change",
        options: [
          { value: "0", label: "Same day" },
          { value: "1", label: "1 day" },
          { value: "7", label: "1 week" },
          { value: "14", label: "2 weeks" },
          { value: "30", label: "1 month" },
        ],
      },
    ],
  },
  {
    key: "action__update_task",
    name: "Update task",
    description: "Update the task",
    category: "task",
    nodeType: "action",
    recordType: "task",
    icon: CheckCheck,
    requiresSubjects: ["task"],
    fields: [
      {
        key: "title",
        label: "Title",
        inputType: "string",
        required: false,
        helperText: "Leave blank to keep the current title",
        supportsInterpolation: true,
      },
      {
        key: "description",
        label: "Description",
        inputType: "markdown",
        required: false,
        supportsInterpolation: true,
      },
      {
        key: "due_in",
        label: "Due in",
        inputType: "select",
        required: false,
        placeholder: "No change",
        options: [
          { value: "0", label: "Same day" },
          { value: "1", label: "1 day" },
          { value: "2", label: "2 days" },
          { value: "3", label: "3 days" },
          { value: "5", label: "5 days" },
          { value: "7", label: "1 week" },
          { value: "14", label: "2 weeks" },
          { value: "30", label: "1 month" },
        ],
      },
      {
        key: "priority",
        label: "Priority",
        inputType: "select",
        required: false,
        placeholder: "No change",
        options: [
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
          { value: "urgent", label: "Urgent" },
        ],
      },
      {
        key: "status",
        label: "Status",
        inputType: "select",
        required: false,
        placeholder: "No change",
        options: [
          { value: "todo", label: "To do" },
          { value: "done", label: "Done" },
          { value: "rejected", label: "Rejected" },
        ],
      },
      {
        key: "assignee",
        label: "Assigned to",
        inputType: "assignment_picker",
        required: false,
        optionsSource: "assignments",
      },
    ],
  },
  {
    key: "action__task_add_comment",
    name: "Add comment",
    description: "Add a comment to the task",
    category: "task",
    nodeType: "action",
    recordType: "task",
    icon: MessageSquare,
    requiresSubjects: ["task"],
    fields: [
      {
        key: "content",
        label: "Comment",
        inputType: "textarea",
        required: true,
        supportsInterpolation: true,
      },
    ],
  },
]
